import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { PL_API, purchaseErrorMessage } from './purchaseUtils';

const MAX_BYTES = 25 * 1024 * 1024;

/**
 * "Lisää lasku" / "Lisää kuitti": upload PDFs, photos or Finvoice XML to the purchase inbox.
 * Phones get a camera button (capture="environment"). `maintenanceId` links the receipt to a
 * work order through the upload context.
 */
export default function UploadDocumentsDialog({ open, onClose, onUploaded, maintenanceId, title }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef(null);
  const cameraInput = useRef(null);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const tooLarge = incoming.find((f) => f.size > MAX_BYTES);
    if (tooLarge) {
      setError(t('purchaseLedger.upload.tooLarge', { name: tooLarge.name }));
      return;
    }
    setError('');
    setFiles((prev) => [...prev, ...incoming].slice(0, 20));
  };

  const reset = () => {
    setFiles([]);
    setError('');
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      if (maintenanceId) form.append('maintenance', String(maintenanceId));
      const response = await axios.post(`${PL_API}/inbox/upload/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded?.(response.data);
      reset();
      onClose();
    } catch (err) {
      setError(purchaseErrorMessage(t, err, 'purchaseLedger.upload.failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="pl-upload-dialog">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title || t('purchaseLedger.upload.title')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('purchaseLedger.upload.help')}
        </Typography>
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
          }}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            addFiles(e.dataTransfer.files);
          }}
          data-testid="pl-upload-dropzone"
        >
          <CloudUploadIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('purchaseLedger.upload.dropzone')}
          </Typography>
        </Box>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="application/pdf,image/*,.xml,text/xml,application/xml"
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
          data-testid="pl-upload-input"
        />
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={() => fileInput.current?.click()}>
            {t('purchaseLedger.upload.chooseFiles')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCameraIcon />}
            onClick={() => cameraInput.current?.click()}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            {t('purchaseLedger.upload.takePhoto')}
          </Button>
        </Box>
        {files.length > 0 && (
          <List dense sx={{ mt: 1 }}>
            {files.map((f, index) => (
              <ListItem
                key={`${f.name}-${index}`}
                secondaryAction={
                  <Button size="small" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}>
                    {t('Remove')}
                  </Button>
                }
              >
                <ListItemText primary={f.name} secondary={`${Math.round(f.size / 1024)} kB`} />
              </ListItem>
            ))}
          </List>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || files.length === 0}
          startIcon={uploading ? <CircularProgress size={16} /> : null}
          data-testid="pl-upload-submit"
        >
          {t('purchaseLedger.upload.submit', { count: files.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
